import type { LucideIcon } from "lucide-react";

import { LicensingModuleKeys } from "../types/licensing.types";
import { NAV_ICONS } from "../components/icons/navIcons";

import { AllActions, AllModules } from "../accessManagement/AccessValues";

export interface NavItem {
  path: string;
  labelKey: string;
  icon: LucideIcon;

  /**
   * TEMPORARY
   * Remove later when fully permission-driven
   */
  roles?: string[];
  action?: number | string;
  moduleName?: number | string;
  /** Shown only while the organisation's plan includes this licensing
   *  module (a LicensingModuleKeys value). Unlike the core module entries,
   *  which stay visible and land on Module Disabled, a Library section for
   *  a module the plan leaves out is simply not listed. */
  requiresPlanModule?: string;
  children?: NavItem[];
  /** Restricts this item to Org Admins / Super Admins regardless of any
   * per-module permission its children might otherwise pass on their own —
   * e.g. Admin's Contract-tagged children (Contract Titles/Categories/...)
   * would stay visible to any user with generic Contract:SEARCH access. */
  adminOnly?: boolean;
  /** Inside an adminOnly group, this child is still listed for a non-admin
   * who holds its module permission (Staffs for a Dept Admin / BU Head). */
  nonAdminAllowed?: boolean;
  /** Narrower than adminOnly — restricts this item to a true Super Admin,
   * hidden even from an Org Admin (unlike adminOnly's items, which an Org
   * Admin's generic bypass would otherwise show regardless of moduleName). */
  superAdminOnly?: boolean;
  /** Shown only while the internal "isEmeis" switch is on (see
   *  utils/emeisFlag) — product areas hidden from customers for now. */
  emeisOnly?: boolean;
  /** Shown only while the "isEmeisClause" switch is on. */
  emeisClauseOnly?: boolean;
  /** Shown only while the "isEmeisCalendar" switch is on (My Calendar). */
  emeisCalendarOnly?: boolean;
  /** Shown only while the "isEmeisSubscription" switch is on (licensing). */
  emeisSubscriptionOnly?: boolean;
  /** Shown only while the "enableEmeisAI" switch is on (see utils/emeisAIFlag). */
  emeisAiOnly?: boolean;
  /** Shown only while the "isEmeisBulk" switch is on (bulk upload of contracts). */
  emeisBulkOnly?: boolean;
  /** Shown only while the "isEmeisSignature" switch is on (Pactline Native Signature). */
  emeisSignatureOnly?: boolean;
  /** Shown only while the "isEmeisMonitor" switch is on (Application Status). */
  emeisMonitorOnly?: boolean;
  /** Paths that belong to this item but do not live under its path, e.g. a record's detail page. */
  alsoMatches?: RegExp[];
}

export const NAV_ITEMS: NavItem[] = [
  // --------------------------------------------------
  // Dashboard
  // --------------------------------------------------
  {
    path: "/",
    labelKey: "dashboard",
    icon: NAV_ICONS.home,
  },

  {
    path: "/dashboard",
    labelKey: "analyticsDashboard",
    icon: NAV_ICONS.analytics,
    moduleName: AllModules.Dashboard,
    action: AllActions.SEARCH,
  },

  // One calendar across contracts, obligations and receivables; each module
  // shows only when the plan includes it (see MyCalendarPage). Behind its
  // own "isEmeisCalendar" switch.
  {
    path: "/my-calendar",
    labelKey: "myCalendar",
    icon: NAV_ICONS.calendar,
    emeisCalendarOnly: true,
  },

  // --------------------------------------------------
  // Smart Drafting
  // --------------------------------------------------
  {
    path: "/contracts-studio",
    labelKey: "contractsStudio",
    icon: NAV_ICONS.contracts,
    moduleName: AllModules.Contract,

    children: [
      {
        path: "/contracts",
        labelKey: "allContracts",
        icon: NAV_ICONS.allContracts,
        moduleName: AllModules.Contract,
        action: AllActions.SEARCH,
      },

      {
        path: "/contracts/legacy-list",
        labelKey: "legacyContracts",
        icon: NAV_ICONS.legacyContract,
        moduleName: AllModules.Contract,
        action: AllActions.SEARCH,
      },
      // Creating is offered from each list's own Create button (the Contracts
      // page's also holds bulk import), not the sidebar.
    ],
  },

  // --------------------------------------------------
  // Obligation Tracker
  // --------------------------------------------------
  {
    path: "/obligations-hub",
    labelKey: "obligationsHub",
    icon: NAV_ICONS.obligations,
    moduleName: AllModules.Obligation,

    children: [
      {
        path: "/obligations-hub/dashboard",
        labelKey: "obligationsDashboard",
        icon: NAV_ICONS.dashboard,
        moduleName: AllModules.Obligation,
      },
      {
        path: "/obligations-hub/my-work",
        labelKey: "obligationsMyWork",
        icon: NAV_ICONS.inbox,
        moduleName: AllModules.Obligation,
      },
      {
        path: "/obligations-hub/all",
        // An obligation's own page and its edit page sit beside the list, not under it.
        alsoMatches: [/^\/obligations-hub\/[0-9a-f-]{36}(\/.*)?$/i],
        labelKey: "allObligations",
        icon: NAV_ICONS.allObligations,
        moduleName: AllModules.Obligation,
      },
    ],
  },

  // --------------------------------------------------
  // Receivables Recovery
  // --------------------------------------------------
  {
    path: "/receivables-engine",
    labelKey: "receivablesEngine",
    icon: NAV_ICONS.receivables,
    moduleName: AllModules.Receivable,

    children: [
      {
        path: "/receivables-engine/dashboard",
        labelKey: "receivablesDashboard",
        icon: NAV_ICONS.dashboard,
        moduleName: AllModules.Receivable,
        action: AllActions.SEARCH,
      },
      {
        path: "/receivables-engine/all",
        // An entry's own page and its edit page sit beside the list, not under it.
        alsoMatches: [/^\/receivables-engine\/[0-9a-f-]{36}(\/.*)?$/i],
        labelKey: "allReceivables",
        icon: NAV_ICONS.allReceivables,
        moduleName: AllModules.Receivable,
        action: AllActions.SEARCH,
      },
    ],
  },
  // --------------------------------------------------
  // Parties — the records every module works with, so they follow the
  // modules and come before the Library of templates
  // --------------------------------------------------
  {
    path: "/party",
    labelKey: "registeredParties",
    icon: NAV_ICONS.customers,
    moduleName: AllModules.Contract,
  },

  // --------------------------------------------------
  // Library — top level, not under Smart Drafting: templates and clauses
  // are authored on their own, not as part of working a single contract.
  // --------------------------------------------------
  {
    path: "/library",
    labelKey: "library",
    icon: NAV_ICONS.library,
    moduleName: AllModules.Template,
    action: AllActions.SEARCH,
    children: [
      // Each kind of template is one entry opening its list; adding one is
      // offered from the list page. The list lives at ".../all" for contract
      // and clause templates, so their detail, edit and new pages are matched
      // by prefix to keep the entry lit.
      {
        path: "/library/contract-templates/all",
        alsoMatches: [/^\/library\/contract-templates\//],
        labelKey: "contractTemplates",
        icon: NAV_ICONS.contractTemplates,
        requiresPlanModule: LicensingModuleKeys.ContractStudio,
        moduleName: AllModules.Template,
        action: AllActions.SEARCH,
      },
      {
        path: "/library/clause-templates/all",
        alsoMatches: [/^\/library\/clause-templates\//],
        emeisClauseOnly: true,
        labelKey: "clauseTemplates",
        icon: NAV_ICONS.clauseTemplates,
        requiresPlanModule: LicensingModuleKeys.ContractStudio,
        moduleName: AllModules.Template,
        action: AllActions.SEARCH,
      },
      // Email and notice templates sit beside contract and clause templates
      // as peers.
      {
        path: "/receivables-engine/templates/email",
        labelKey: "emailTemplates",
        icon: NAV_ICONS.emailTemplate,
        requiresPlanModule: LicensingModuleKeys.ReceivablesEngine,
        moduleName: AllModules.ReceivableTemplate,
        action: AllActions.SEARCH,
      },
      {
        path: "/receivables-engine/templates/notice",
        labelKey: "noticeTemplates",
        icon: NAV_ICONS.noticeTemplate,
        requiresPlanModule: LicensingModuleKeys.ReceivablesEngine,
        moduleName: AllModules.ReceivableTemplate,
        action: AllActions.SEARCH,
      },
    ],
  },

  // --------------------------------------------------
  // Notifications
  // --------------------------------------------------
  {
    path: "/notifications",
    labelKey: "notifications",
    icon: NAV_ICONS.notification,
    // moduleName: AllModules.Notification,
  },

  // --------------------------------------------------
  // Help — step-by-step guides, loaded on demand from
  // public/content/help.
  // Internal for now; any signed-in user once the flag comes off.
  // --------------------------------------------------
  {
    path: "/help",
    emeisOnly: true,
    labelKey: "help",
    icon: NAV_ICONS.help,
  },

  // --------------------------------------------------
  // Admin
  // --------------------------------------------------
  {
    path: "/admin",
    labelKey: "admin",
    icon: NAV_ICONS.admin,
    adminOnly: true,

    children: [
      // Organisation: who works here and how they are grouped.
      {
        path: "/admin/organization",
        labelKey: "organisationSettings",
        icon: NAV_ICONS.organisation,
        moduleName: AllModules.Group,
      },
      {
        path: "/admin/staffs",
        labelKey: "staffs",
        icon: NAV_ICONS.staffs,
        moduleName: AllModules.Member,
        nonAdminAllowed: true,
      },
      {
        path: "/admin/businessUnit",
        labelKey: "businessUnit",
        icon: NAV_ICONS.businessUnit,
        moduleName: AllModules.BusinessUnit,
      },
      {
        path: "/admin/department",
        labelKey: "department",
        icon: NAV_ICONS.department,
        moduleName: AllModules.Department,
      },
      {
        path: "/admin/roles",
        labelKey: "roleManagement",
        icon: NAV_ICONS.roles,
        moduleName: AllModules.Role,
        adminOnly: true,
      },

      // Contract master data: the lists contract forms pick from.
      {
        path: "/admin/contract-title",
        labelKey: "contractTitles",
        icon: NAV_ICONS.conractTitle,
        moduleName: AllModules.Contract,
      },
      {
        path: "/admin/contract-category",
        labelKey: "contractCategories",
        icon: NAV_ICONS.contractCategories,
        moduleName: AllModules.Contract,
      },
      {
        path: "/admin/contract-type",
        labelKey: "contractTypes",
        icon: NAV_ICONS.contractType,
        moduleName: AllModules.Contract,
      },
      // Master data for the Clause Library (only while "isEmeisClause" is on).
      {
        path: "/admin/clause-type",
        emeisClauseOnly: true,
        labelKey: "clauseTypes",
        icon: NAV_ICONS.clauseType,
        moduleName: AllModules.Contract,
      },
      {
        path: "/admin/jurisdiction",
        emeisClauseOnly: true,
        labelKey: "jurisdictions",
        icon: NAV_ICONS.jurisdiction,
        moduleName: AllModules.Contract,
      },
      {
        path: "/admin/entity-type",
        labelKey: "entityTypes",
        icon: NAV_ICONS.entityType,
        moduleName: AllModules.Contract,
      },

      // Settings and platform tools.
      // Settings → E-Signature / Signing: the organisation's default provider
      // and Pactline Native Signature's methods, authentication and audit.
      {
        path: "/admin/signing",
        labelKey: "eSignatureSettings",
        icon: NAV_ICONS.eSignature,
        adminOnly: true,
        emeisSignatureOnly: true,
      },
      // Workflow rules of the modules, one sub-item per module — today the
      // Receivables Recovery's settings (approvals, who may act, disposition
      // rules, default plans).
      {
        path: "/admin/workflow",
        labelKey: "workflow",
        icon: NAV_ICONS.workflow,
        superAdminOnly: true,
        children: [
          {
            path: "/admin/workflow/receivables",
            labelKey: "receivablesSettings",
            icon: NAV_ICONS.receivables,
            superAdminOnly: true,
          },
        ],
      },
      {
        path: "/admin/trigger-reminders",
        labelKey: "triggerReminders",
        icon: NAV_ICONS.renewalReminder,
        adminOnly: true,
        alsoMatches: [/^\/admin\/(renewal|obligation)-reminders/],
      },
      // Platform AI settings, one sub-item per screen — today the provider
      // accounts, API keys and models.
      {
        path: "/admin/ai",
        labelKey: "ai",
        icon: NAV_ICONS.ai,
        superAdminOnly: true,
        emeisAiOnly: true,
        children: [
          {
            path: "/admin/ai-models",
            labelKey: "aiModels",
            icon: NAV_ICONS.aiKeys,
            superAdminOnly: true,
            emeisAiOnly: true,
          },
          {
            path: "/admin/ai-agents",
            labelKey: "aiAgents",
            icon: NAV_ICONS.aiAgents,
            superAdminOnly: true,
            emeisAiOnly: true,
          },
        ],
      },
      // Server monitor: services of a host over SSH (super admin, isEmeisMonitor).
      {
        path: "/admin/application-status",
        labelKey: "applicationStatus",
        icon: NAV_ICONS.applicationStatus,
        superAdminOnly: true,
        emeisMonitorOnly: true,
      },
      // Licensing: the organisation's own plan, usage and recharges (Org
      // Admin), and the platform-wide plan/pack/entitlement console (Super
      // Admin only).
      {
        path: "/admin/subscription",
        emeisSubscriptionOnly: true,
        labelKey: "subscriptionUsage",
        icon: NAV_ICONS.subscription,
        adminOnly: true,
      },
      {
        path: "/admin/licensing",
        emeisSubscriptionOnly: true,
        labelKey: "licenseManagement",
        icon: NAV_ICONS.licensing,
        moduleName: AllModules.Group,
        superAdminOnly: true,
      },
      {
        path: "/admin/providers",
        emeisOnly: true,
        labelKey: "manageProviders",
        icon: NAV_ICONS.providers,
        moduleName: AllModules.Group,
        superAdminOnly: true,
      },
      {
        path: "/admin/reindex",
        emeisMonitorOnly: true,
        labelKey: "reindexData",
        icon: NAV_ICONS.refresh,
        superAdminOnly: true,
      },
    ],
  },
];

// --------------------------------------------------
// Routes
// --------------------------------------------------

const flattenNavItems = (
  items: NavItem[],
): [
  string,
  {
    labelKey: string;
    Icon: LucideIcon;
  },
][] =>
  items.flatMap((item) => [
    [
      item.path,
      {
        labelKey: item.labelKey,
        Icon: item.icon,
      },
    ] as [
      string,
      {
        labelKey: string;
        Icon: LucideIcon;
      },
    ],

    ...(item.children ? flattenNavItems(item.children) : []),
  ]);

export const ROUTES = Object.fromEntries(flattenNavItems(NAV_ITEMS));
